<script setup lang="ts">
  import { useDialogA11y } from '@/composables/useDialogA11y'
  import { useTranslation } from '@/composables/useTranslation'
  import type { CustomerCalendar, TUser } from '@/types/generated'
  import CalendarSettingsPanel from '@/components/app/calendar/CalendarSettingsPanel.vue'
  import { computed } from 'vue'

  type EntryKind = 'termin' | 'frist' | 'wiedervorlage'

  const props = withDefaults(
    defineProps<{
      open: boolean
      calendars: CustomerCalendar[]
      calendarKindChecks?: Record<string, Record<EntryKind, boolean>>
      currentUser?: TUser | null
      disconnectingAccountUuid?: string | null
      accountsLoading?: boolean
    }>(),
    {
      currentUser: null,
      disconnectingAccountUuid: null,
      accountsLoading: false,
      calendarKindChecks: () => ({})
    }
  )

  const emit = defineEmits<{
    close: []
    'disconnect-account': [accountUuid: string]
    'set-account-kind-sync': [
      payload: { calendarUuids: string[]; kind: EntryKind; checked: boolean }
    ]
  }>()

  const requestClose = (): void => {
    emit('close')
  }

  const { dialogRef, onDialogKeydown } = useDialogA11y(
    computed(() => props.open),
    requestClose
  )

  const { t } = useTranslation({
    de: {
      close: 'Schließen',
      title: 'Kalendereinstellungen'
    },
    en: {
      close: 'Close',
      title: 'Calendar settings'
    }
  })
</script>

<template>
  <Teleport to="body">
    <div
      v-if="open"
      class="fixed inset-0 z-[86] flex items-center justify-center bg-black/35 p-4 backdrop-blur-[1px]"
      @click.self="requestClose"
    >
      <div
        ref="dialogRef"
        role="dialog"
        aria-modal="true"
        aria-labelledby="calendar-settings-modal-title"
        tabindex="-1"
        class="calendar-main border-layer-line bg-layer relative flex max-h-[92vh] w-full max-w-3xl flex-col
          overflow-hidden rounded-xl border shadow-2xl"
        @keydown="onDialogKeydown"
      >
        <h2
          id="calendar-settings-modal-title"
          class="sr-only"
        >
          {{ t('title') }}
        </h2>
        <button
          type="button"
          class="text-muted-foreground-1 hover:bg-muted-hover focus:outline-hidden absolute end-2.5 top-2.5 z-10
            inline-flex size-7 items-center justify-center rounded-md"
          :aria-label="t('close')"
          @click="requestClose"
        >
          <svg
            class="size-4 shrink-0"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M18 6 6 18" />
            <path d="m6 6 12 12" />
          </svg>
        </button>

        <CalendarSettingsPanel
          :key="open ? 'open' : 'closed'"
          :calendars="props.calendars"
          :calendar-kind-checks="props.calendarKindChecks"
          :current-user="props.currentUser"
          :disconnecting-account-uuid="props.disconnectingAccountUuid"
          :accounts-loading="props.accountsLoading"
          :restore-modal-on-return="true"
          class="min-h-0 grow overflow-y-auto"
          @disconnect-account="emit('disconnect-account', $event)"
          @set-account-kind-sync="emit('set-account-kind-sync', $event)"
        />
      </div>
    </div>
  </Teleport>
</template>
